// Gender + garment-type resolution for the /men and /women shops, the mega
// menu and the homepage pairs. Pure and client-safe (no server-only imports).
//
// Gender source of truth, in order:
//   1) Wix collections "Men's" / "Women's" (a product in both is unisex).
//   2) The product-name classifier (lib/categories.ts mens/womens keywords),
//      or the word "unisex" in the name.
//   3) No signal at all -> both. This is a unisex boutique; an untagged piece
//      is shown to everyone rather than hidden from everyone.
//
// Garment type comes from the name classifier, refined into sub-types for
// tops, with the Wix type collections (Tops/Bottoms/Outerwear/Accessories) as
// the fallback when the name says nothing.

import type { CatalogLite } from "./types";
import { categorize } from "./categories";
import { brandSlug } from "./brands";

export type Gender = "men" | "women";

export interface GenderInfo {
  slug: Gender;
  /** "Men" — nav label, breadcrumb. */
  label: string;
  /** "Men's" — page titles and copy. */
  possessive: string;
}

export const GENDERS: GenderInfo[] = [
  { slug: "men", label: "Men", possessive: "Men's" },
  { slug: "women", label: "Women", possessive: "Women's" },
];

export function isGender(s: string): s is Gender {
  return s === "men" || s === "women";
}

export function genderInfo(g: Gender): GenderInfo {
  return GENDERS.find((x) => x.slug === g)!;
}

/** "Men’s" / "Mens" / "MEN'S" -> "mens". */
function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z]/g, "");
}

/** Which gender shops a product appears in (one or both). */
export function gendersOf(p: Pick<CatalogLite, "name" | "collections">): Gender[] {
  const cols = new Set((p.collections ?? []).map(norm));
  const m = cols.has("mens") || cols.has("men");
  const w = cols.has("womens") || cols.has("women");
  if (m || w) return [...(m ? (["men"] as const) : []), ...(w ? (["women"] as const) : [])];

  if (/\bunisex\b/i.test(p.name)) return ["men", "women"];
  const cats = categorize(p.name);
  const cm = cats.includes("mens");
  const cw = cats.includes("womens");
  if (cm && !cw) return ["men"];
  if (cw && !cm) return ["women"];
  return ["men", "women"];
}

// ---------------------------------------------------------------------------
// Garment taxonomy (ordered = menu order)
// ---------------------------------------------------------------------------

export type TypeGroupSlug = "tops" | "bottoms" | "accessories";

export interface GarmentType {
  slug: string;
  label: string;
  group?: TypeGroupSlug;
}

export const TYPE_GROUPS: { slug: TypeGroupSlug; label: string }[] = [
  { slug: "tops", label: "Tops" },
  { slug: "bottoms", label: "Bottoms" },
  { slug: "accessories", label: "Accessories" },
];

export const GARMENT_TYPES: GarmentType[] = [
  { slug: "tees", label: "Tees", group: "tops" },
  { slug: "polos", label: "Polos", group: "tops" },
  { slug: "shirts", label: "Shirts", group: "tops" },
  { slug: "hoodies", label: "Hoodies & Sweatshirts", group: "tops" },
  { slug: "jeans", label: "Jeans", group: "bottoms" },
  { slug: "pants", label: "Pants", group: "bottoms" },
  { slug: "shorts", label: "Shorts", group: "bottoms" },
  { slug: "sets", label: "Sets" },
  { slug: "dresses", label: "Dresses & Skirts" },
  { slug: "jumpsuits", label: "Jumpsuits" },
  { slug: "outerwear", label: "Outerwear" },
  { slug: "shapewear", label: "Bodysuits & Shapewear" },
  { slug: "swimwear", label: "Swimwear" },
  { slug: "footwear", label: "Footwear" },
  { slug: "caps", label: "Caps & Hats", group: "accessories" },
  { slug: "beanies", label: "Beanies", group: "accessories" },
  { slug: "fragrances", label: "Fragrances" },
];

export interface Classified {
  /** Specific type slug from GARMENT_TYPES, when one is known. */
  type?: string;
  /** Tops/Bottoms/Accessories group, when the garment belongs to one. */
  group?: TypeGroupSlug;
}

const TEE_RE = /\b(tee|tees|t-?shirts?|tshirts?|camisetas?)\b/i;
const POLO_RE = /\b(polos?|poloche)\b/i;
const SHIRT_RE = /\b(shirts?|camisas?|blouses?|blusas?|button[- ]?(up|down))\b/i;
const SWEAT_RE = /\b(sweatshirts?|crew ?necks?|pullovers?)\b/i;

const BEANIE_RE = /\b(beanies|beanie|gorros?|knit hat)\b/i;
const CAP_RE = /\b(hats?|caps?|gorras?|snapbacks?|truckers?|visors?|bucket hat|fitted)\b/i;

/** Accessories split into headwear sub-types; bags/jewelry/belts stay group-only. */
function accessoryType(name: string): Classified {
  if (BEANIE_RE.test(name)) return { type: "beanies", group: "accessories" };
  if (CAP_RE.test(name)) return { type: "caps", group: "accessories" };
  return { group: "accessories" };
}

const groupOf = (type: string): TypeGroupSlug | undefined =>
  GARMENT_TYPES.find((t) => t.slug === type)?.group;

/** Resolve one product to its garment type and group. */
export function classify(p: Pick<CatalogLite, "name" | "collections">): Classified {
  const cats = categorize(p.name);
  const has = (s: string) => cats.includes(s);

  if (has("tops")) {
    if (POLO_RE.test(p.name)) return { type: "polos", group: "tops" };
    if (TEE_RE.test(p.name)) return { type: "tees", group: "tops" };
    if (SHIRT_RE.test(p.name)) return { type: "shirts", group: "tops" };
    return { group: "tops" };
  }
  if (has("hoodies")) return { type: "hoodies", group: "tops" };
  if (has("outerwear")) {
    return SWEAT_RE.test(p.name) ? { type: "hoodies", group: "tops" } : { type: "outerwear" };
  }
  if (has("skirts")) return { type: "dresses" };
  if (has("accessories")) return accessoryType(p.name);
  for (const t of ["jeans", "pants", "shorts", "sets", "dresses", "jumpsuits", "shapewear", "swimwear", "footwear", "fragrances"]) {
    if (has(t)) return { type: t, group: groupOf(t) };
  }

  // Name said nothing: fall back to the Wix type collections.
  const cols = new Set((p.collections ?? []).map(norm));
  if (cols.has("tops")) return { group: "tops" };
  if (cols.has("bottoms")) return { group: "bottoms" };
  if (cols.has("outerwear")) return { type: "outerwear" };
  if (cols.has("beanies")) return { type: "beanies", group: "accessories" };
  if (cols.has("headwear") || cols.has("caps")) return { type: "caps", group: "accessories" };
  if (cols.has("accessories")) return { group: "accessories" };
  return {};
}

/** True when `slug` (a type slug or "tops"/"bottoms") covers the product. */
export function matchesType(p: CatalogLite, slug: string): boolean {
  const c = classify(p);
  return c.type === slug || c.group === slug;
}

/** Label for a type or group slug, or undefined when the slug is unknown. */
export function typeLabel(slug: string): string | undefined {
  return (
    TYPE_GROUPS.find((g) => g.slug === slug)?.label ?? GARMENT_TYPES.find((t) => t.slug === slug)?.label
  );
}

// ---------------------------------------------------------------------------
// Product sets
// ---------------------------------------------------------------------------

/** Newest first by Wix creation date (undated rows sink to the end). */
export function byNewest(a: CatalogLite, b: CatalogLite): number {
  return (b.created ?? "").localeCompare(a.created ?? "");
}

/** True for headwear, bags, jewelry and the rest of the Accessories group. */
export function isAccessory(p: Pick<CatalogLite, "name" | "collections">): boolean {
  return classify(p).group === "accessories";
}

/**
 * Every product shown in a gender shop, newest first. Accessories are not
 * clothing for either shop: they live at /accessories, including products
 * with no Men's/Women's collection (which would otherwise fall into both).
 */
export function genderProducts(index: CatalogLite[], g: Gender): CatalogLite[] {
  return index.filter((p) => gendersOf(p).includes(g) && !isAccessory(p)).sort(byNewest);
}

/** Every accessory, newest first (the /accessories shop). */
export function accessoryProducts(index: CatalogLite[]): CatalogLite[] {
  return index.filter(isAccessory).sort(byNewest);
}

/** Accessories of one sub-type slug ("caps", "beanies"), newest first. */
export function accessoryTypeProducts(index: CatalogLite[], slug: string): CatalogLite[] {
  return accessoryProducts(index).filter((p) => classify(p).type === slug);
}

/** True when slug is the Accessories group or one of its sub-types. */
export function isAccessorySlug(slug: string): boolean {
  return slug === "accessories" || GARMENT_TYPES.some((t) => t.slug === slug && t.group === "accessories");
}

/** Products of one gender and type/group slug, newest first. */
export function genderTypeProducts(index: CatalogLite[], g: Gender, slug: string): CatalogLite[] {
  return genderProducts(index, g).filter((p) => matchesType(p, slug));
}

export const NEW_ARRIVAL_DAYS = 60;

/** Floor for new arrivals when nothing landed inside the window. */
export const NEW_ARRIVAL_FLOOR = 8;

/**
 * New arrivals: IN-STOCK products created in the last NEW_ARRIVAL_DAYS, newest
 * first, capped at `max`. When nothing is that recent it falls back to the
 * newest NEW_ARRIVAL_FLOOR in-stock products, so the "New Arrivals" links and
 * routes never dead-end while something can be bought.
 *
 * Sold-out pieces are excluded outright, never back-filled: "new" means "new
 * and buyable" everywhere this feeds (homepage rail, NEW IN panel,
 * /new-arrivals, the gender new-arrivals pages, the sitemap). They stay
 * browsable on brand, type and product pages. Empty when nothing is in stock,
 * and every caller hides its section instead of showing an empty one.
 */
export function newArrivals(list: CatalogLite[], max = 24, now = Date.now()): CatalogLite[] {
  const cutoff = new Date(now - NEW_ARRIVAL_DAYS * 864e5).toISOString();
  const sorted = list.filter((p) => p.inStock).sort(byNewest);
  const recent = sorted.filter((p) => (p.created ?? "") >= cutoff);
  return (recent.length ? recent : sorted.slice(0, NEW_ARRIVAL_FLOOR)).slice(0, max);
}

export interface TypeCount {
  slug: string;
  label: string;
  count: number;
}

export interface GroupCount extends TypeCount {
  slug: TypeGroupSlug;
  /** Populated sub-types inside the group, in taxonomy order. */
  types: TypeCount[];
}

export interface PopulatedTypes {
  /** Tops / Bottoms / Accessories with at least one product. */
  groups: GroupCount[];
  /** Populated types that sit outside a group (Sets, Outerwear, …). */
  singles: TypeCount[];
}

/** Which types and groups hold products, for a product list (e.g. one gender). */
export function populatedTypes(list: CatalogLite[]): PopulatedTypes {
  const typeCount = new Map<string, number>();
  const groupCount = new Map<string, number>();
  for (const p of list) {
    const c = classify(p);
    if (c.type) typeCount.set(c.type, (typeCount.get(c.type) ?? 0) + 1);
    if (c.group) groupCount.set(c.group, (groupCount.get(c.group) ?? 0) + 1);
  }
  const groups: GroupCount[] = TYPE_GROUPS.filter((g) => groupCount.has(g.slug)).map((g) => ({
    slug: g.slug,
    label: g.label,
    count: groupCount.get(g.slug)!,
    types: GARMENT_TYPES.filter((t) => t.group === g.slug && typeCount.has(t.slug)).map((t) => ({
      slug: t.slug,
      label: t.label,
      count: typeCount.get(t.slug)!,
    })),
  }));
  const singles = GARMENT_TYPES.filter((t) => !t.group && typeCount.has(t.slug)).map((t) => ({
    slug: t.slug,
    label: t.label,
    count: typeCount.get(t.slug)!,
  }));
  return { groups, singles };
}

/** Every type/group slug that holds products for a gender (for routes + sitemap). */
export function populatedTypeSlugs(index: CatalogLite[], g: Gender): string[] {
  const { groups, singles } = populatedTypes(genderProducts(index, g));
  return [...groups.flatMap((gr) => [gr.slug, ...gr.types.map((t) => t.slug)]), ...singles.map((s) => s.slug)];
}

export interface Drop {
  brandSlug: string;
  brand: string;
  /** Newest creation date among the brand's products. */
  created: string;
  products: CatalogLite[];
}

/**
 * The latest drop: the brand whose newest product is the most recent, with
 * that brand's products newest first. Brandless products are ignored.
 */
export function latestDrop(list: CatalogLite[]): Drop | null {
  const by = new Map<string, Drop>();
  for (const p of list) {
    const slug = brandSlug(p.brand);
    if (!slug || !p.brand) continue;
    const d = by.get(slug) ?? { brandSlug: slug, brand: p.brand, created: "", products: [] };
    d.products.push(p);
    if ((p.created ?? "") > d.created) d.created = p.created ?? "";
    by.set(slug, d);
  }
  const drops = [...by.values()].sort((a, b) => b.created.localeCompare(a.created));
  const top = drops[0];
  if (!top) return null;
  top.products.sort(byNewest);
  return top;
}
